import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ShopApiService } from '@digilize/shared/data/services';
import { AlertService } from '@digilize/shared/feature/modules/alert';
import { TranslocoService } from '@ngneat/transloco';
import { Alert, AlertType, Page, PageType } from '@shared/definitions';
import { first, forkJoin, Subject } from 'rxjs';

@Component({
  selector: 'lib-dialog-pages',
  templateUrl: './dialog-pages.component.html',
  styleUrls: ['./dialog-pages.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DialogPagesComponent implements OnInit {
  takeUntil$ = new Subject();

  loading = true;
  pageType: PageType;
  shopId;
  pages: Page[] = [];
  pagesSelect: any[] = [];
  form: UntypedFormGroup = new UntypedFormGroup({
    page: new UntypedFormControl('', Validators.required),
  });

  constructor(
    public dialogRef: MatDialogRef<DialogPagesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private shopApiService: ShopApiService,
    private alertService: AlertService,
    private router: Router,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.initPages();
  }

  initPages() {
    if (this.data.pages && this.data.shopId && this.data.pageType) {
      this.shopId = this.data.shopId;
      this.pages = this.data.pages;
      this.pageType = this.data.pageType;
      this.pages.forEach((obj) => {
        if (this.pageType === PageType.Facebook) {
          this.pagesSelect.push({
            key: obj.facebook_page_name,
            value: obj.facebook_page_id,
            img: obj.facebook_page_picture_url,
          });
        } else if (this.pageType === PageType.Instagram) {
          this.pagesSelect.push({
            key: obj.instagram_page_name,
            value: obj.instagram_page_id,
            img: obj.instagram_page_picture_url,
          });
        }
      });
    }

    if (this.pages && this.data.shopId) {
      if (this.pages.length === 1) {
        // Get 1st page if there's only one
        if (this.pageType === PageType.Facebook) {
          this.form.get('page').setValue(this.pages[0].facebook_page_id);
        } else if (this.pageType === PageType.Instagram) {
          this.form.get('page').setValue(this.pages[0].instagram_page_id);
        }
        this.onSubmit();
      } else if (this.pages.length === 0) {
        // Reset pages when there's no pages
        this.onSubmit(true);
      } else {
        // Display select if there are multiple pages
        this.loading = false;
      }
    }
  }

  closeDialog(data?) {
    this.dialogRef.close(data);
  }

  onSubmit(reset?: boolean) {
    this.loading = true;
    const returnUrl = localStorage.getItem('returnUrl').replace('/admin', '');
    let page: Page;
    if (reset) {
      page = {
        facebook_user_access_token: null,
        facebook_page_access_token: null,
        facebook_page_name: null,
        facebook_page_id: null,
        facebook_page_picture_url: null,
        instagram_page_id: null,
        instagram_page_username: null,
        instagram_page_name: null,
        instagram_page_picture_url: null,
      };
    } else {
      if (this.pageType === PageType.Facebook) {
        page = this.pages.find((obj) => obj.facebook_page_id === this.form.get('page').value);
      } else if (this.pageType === PageType.Instagram) {
        page = this.pages.find((obj) => obj.instagram_page_id === this.form.get('page').value);
      }
    }
    const noPageConnectedText$ = this.translocoService
      .selectTranslate('social_media_connection.no_page_connected', { pageType: this.pageType })
      .pipe(first());
    forkJoin([this.shopApiService.connectShopToPage(this.shopId, page, this.pageType), noPageConnectedText$]).subscribe(
      ([response, noPageConnectedText]) => {
        this.router.navigate([returnUrl]);
        this.closeDialog();

        if (reset) {
          // Empty list of pages and success request
          const alert: Alert = {
            type: AlertType.Warning,
            title: {
              message: 'Warning',
              translate: false,
            },
            message: {
              message: noPageConnectedText,
              translate: false,
            },
          };
          this.alertService.setAlert(alert);
        }
      },
      (error) => {
        this.router.navigate([returnUrl]);
        this.handleError(error.error.error);
        this.closeDialog();
      }
    );
  }

  handleError(error: SocialError) {
    let message;
    const expiredText$ = this.translocoService
      .selectTranslate('social_media_connection.token_expired', { pageType: this.pageType })
      .pipe(first());
    const noContentPublishText$ = this.translocoService
      .selectTranslate('social_media_connection.no_instagram_content_publish', { pageType: this.pageType })
      .pipe(first());
    const multiplePermissionNotText$ = this.translocoService
      .selectTranslate('social_media_connection.multiple_permissions_not_granted', {
        pageType: this.pageType,
        permissions: error.info?.missing.toString(),
      })
      .pipe(first());

    forkJoin([expiredText$, noContentPublishText$, multiplePermissionNotText$]).subscribe(
      ([expiredText, noContentPublishText, multiplePermissionNotText]) => {
        if (error.info?.kind === 'token_invalid') {
          // Invalid token
          message = expiredText;
        } else if (error.info?.missing?.length === 1 && error.info?.missing[0] === 'instagram_content_publish') {
          // Instagram content publish is only missing permission
          message = noContentPublishText;
        } else if (error.info?.missing?.length > 0) {
          // Multiple permissions not granted
          message = multiplePermissionNotText;
        }

        if (message) {
          const alert: Alert = {
            type: AlertType.Warning,
            title: {
              message: 'Warning',
              translate: false,
            },
            message: {
              message: message,
              translate: false,
            },
          };
          this.alertService.setAlert(alert);
        } else {
          // For some other error
          this.alertService.handleApiError(error);
        }
      }
    );
  }
}

export interface SocialError {
  info: {
    field?: string;
    got?: string[];
    kind?: string;
    missing?: string[];
    required?: string[];
  };
  request_id: string;
  type: string;
}
